import { cn } from '@/lib/utils';
import { useEffect, useState } from 'react';

interface WaterTankSectionProps {
  waterLevel: number;
  chemicalLevel?: { current: number; low: number; high: number };
  undergroundWaterLevel?: number;
  temperature?: number;
  inflow?: number;
  isLoading?: boolean;
  pressureSensorError: boolean; // 압력
  electricWaveError: boolean; // 차단기
  externalError: boolean; // 외부 이상
  circuitBreakerError: boolean; // 차단기 이상
  undergroundWaterError: boolean; // 지하수 이상
  overloadError: boolean; // 과부하 이상
  preparation: boolean; // 예비1
  preparation2: boolean; // 예비2
  alias: {
    circuitBreaker: string;
    pressureSensor: string;
    external: string;
    preparation: string;
    preparation2: string;
  };
}

export default function WaterTankSection({
  waterLevel,
  chemicalLevel,
  undergroundWaterLevel,
  temperature,
  inflow,
  isLoading = false,
  // pressureSensorError,
  electricWaveError,
  // externalError,
  // circuitBreakerError,
  undergroundWaterError,
  overloadError,
  // alias,
  // preparation,
  // preparation2,
}: WaterTankSectionProps) {
  const [tankFillAnimation, setTankFillAnimation] = useState(false);

  // waterLevel이 변경될 때 탱크 애니메이션 트리거
  useEffect(() => {
    setTankFillAnimation(false);
    const startAnimation = setTimeout(() => {
      setTankFillAnimation(true);
    }, 200);

    // 60초마다 애니메이션 재실행
    const interval = setInterval(() => {
      setTankFillAnimation(false);
      setTimeout(() => {
        setTankFillAnimation(true);
      }, 300);
    }, 60000);

    return () => {
      clearTimeout(startAnimation);
      clearInterval(interval);
    };
  }, [waterLevel]);

  return (
    <div className="bg-gray-50 rounded-xl p-6">
      <h3 className="text-lg font-semibold text-gray-900 mb-4">
        {'현재 탱크 수위'}
      </h3>

      {
        <>
          <div className="flex items-center justify-center mb-4">
            <div className="relative flex flex-col items-center">
              <div className="relative w-40 h-48 border-4 border-gray-300 rounded-b-xl bg-gray-100 overflow-hidden">
                {/* 수위 눈금 */}
                <div className="absolute left-0 top-2 w-full">
                  {[100, 75, 50, 25].map((level) => (
                    <div
                      key={level}
                      className="relative"
                      style={{ top: `${(100 - level) * 1.76}px` }}
                    >
                      <div className="absolute left-2 w-6 h-px bg-gray-400"></div>
                      <div className="absolute left-10 text-xs text-gray-500 -mt-2">
                        {level}%
                      </div>
                    </div>
                  ))}
                </div>

                {/* 물 애니메이션 */}
                <div
                  className={`absolute bottom-0 w-full bg-gradient-to-t from-blue-500 to-blue-400 transition-all duration-1500 ease-out ${
                    tankFillAnimation ? 'opacity-100' : 'opacity-0'
                  }`}
                  style={{
                    height: tankFillAnimation ? `${waterLevel}%` : '0%',
                    transitionDuration: tankFillAnimation ? '1500ms' : '100ms',
                  }}
                >
                  <div className="absolute top-0 w-full h-1 bg-blue-300 animate-pulse"></div>
                  <div className="absolute inset-0 opacity-30">
                    {[...Array(6)].map((_, i) => (
                      <div
                        key={i}
                        className="absolute w-1 h-1 bg-white rounded-full animate-bounce"
                        style={{
                          left: `${20 + i * 20}%`,
                          animationDelay: `${i * 0.2}s`,
                          animationDuration: '2s',
                        }}
                      ></div>
                    ))}
                  </div>
                </div>

                {/* 수위 표시 */}
                <div className="absolute inset-0 flex items-center justify-center">
                  <div className="bg-white/90 backdrop-blur-sm rounded-lg px-3 py-2 shadow-lg">
                    <span className="text-2xl font-bold text-gray-900">
                      {waterLevel}%
                    </span>
                  </div>
                </div>
              </div>

              {/* 탱크 바닥 */}
              <div className="w-44 h-4 bg-gray-400 rounded-b-lg -mt-1 mx-auto shadow-lg"></div>
            </div>
          </div>

          <div className="text-center">
            <p className="text-sm text-gray-600">물탱크 수위</p>
            {isLoading && (
              <div className="text-xs text-gray-400 mt-1">업데이트 중...</div>
            )}
          </div>
        </>
      }

      {/* 추가 센서 정보 */}
      <div className="mt-4 space-y-2">
        {/* 약품 수위 */}
        {/* {chemicalLevel && (
          <div className="bg-white rounded-lg p-3 border border-gray-200">
            <div className="flex items-center justify-between">
              <span className="text-m text-gray-600">약품 수위</span>
              <span className="text-m font-semibold text-blue-600">
                {chemicalLevel.current}%
              </span>
            </div>
            <div className="flex justify-between text-m text-gray-500 mt-1">
              <span>저: {chemicalLevel.low}%</span>
              <span>고: {chemicalLevel.high}%</span>
            </div>
          </div>
        )} */}

        {/* 지하수위 */}
        {undergroundWaterLevel !== undefined && (
          <div className="bg-white rounded-lg p-3 border border-gray-200">
            <div className="flex items-center justify-between">
              <span className="text-m text-gray-600">지하수위</span>
              <span className="text-m font-semibold text-sky-600">
                {undergroundWaterLevel}%
              </span>
            </div>
          </div>
        )}

        {/* 전파(수신) 이상 */}
        {
          <div className="bg-white rounded-lg p-3 border border-gray-200">
            <div className="flex items-center justify-between">
              <span className="text-m text-gray-600">수신 상태</span>
              <span
                className={cn(
                  'text-m font-semibold',
                  electricWaveError ? 'text-red-600' : 'text-green-600'
                )}
              >
                {electricWaveError ? '이상' : '정상'}
              </span>
            </div>
          </div>
        }

        {/* 온도 */}
        {temperature !== undefined && (
          <div className="bg-white rounded-lg p-3 border border-gray-200">
            <div className="flex items-center justify-between">
              <span className="text-m text-gray-600">온도</span>
              <span className="text-m font-semibold text-orange-600">
                {temperature}°C
              </span>
            </div>
          </div>
        )}

        {/* 과부하 이상 */}
        {
          <div className="bg-white rounded-lg p-3 border border-gray-200">
            <div className="flex items-center justify-between">
              <span className="text-m text-gray-600">과부하</span>
              <span
                className={cn(
                  'text-m font-semibold',
                  overloadError ? 'text-red-600' : 'text-green-600'
                )}
              >
                {overloadError ? '이상' : '정상'}
              </span>
            </div>
          </div>
        }
        {/* 지하수 이상 */}
        {
          <div className="bg-white rounded-lg p-3 border border-gray-200">
            <div className="flex items-center justify-between">
              <span className="text-m text-gray-600">지하수 </span>
              <span
                className={cn(
                  'text-m font-semibold',
                  undergroundWaterError ? 'text-red-600' : 'text-green-600'
                )}
              >
                {undergroundWaterError ? '이상' : '정상'}
              </span>
            </div>
          </div>
        }

        {/* 차단기 */}
        {/* {
          <div className="bg-white rounded-lg p-3 border border-gray-200">
            <div className="flex items-center justify-between">
              <span className="text-m text-gray-600">
                {alias.circuitBreaker || '차단기'}
              </span>
              <span
                className={cn(
                  'text-m font-semibold',
                  circuitBreakerError ? 'text-red-600' : 'text-green-600'
                )}
              >
                {circuitBreakerError ? '이상' : '정상'}
              </span>
            </div>
          </div>
        } */}

        {/* 유입량 */}
        {/* {inflow !== undefined && inflow > 0 && (
          <div className="bg-white rounded-lg p-3 border border-gray-200">
            <div className="flex items-center justify-between">
              <span className="text-m text-gray-600">유입량</span>
              <span className="text-m font-semibold text-purple-600">
                {inflow}
              </span>
            </div>
          </div>
        )} */}

        {/* 외부 이상 */}
        {/* {
          <div className="bg-white rounded-lg p-3 border border-gray-200">
            <div className="flex items-center justify-between">
              <span className="text-m text-gray-600">
                {alias.external || '외부'}
              </span>
              <span
                className={cn(
                  'text-m font-semibold',
                  externalError ? 'text-red-600' : 'text-green-600'
                )}
              >
                {externalError ? '이상' : '정상'}
              </span>
            </div>
          </div>
        } */}

        {/* 압력센서 이상 */}
        {/* {
          <div className="bg-white rounded-lg p-3 border border-gray-200">
            <div className="flex items-center justify-between">
              <span className="text-m text-gray-600">
                {alias.pressureSensor || '압력 센서'}
              </span>
              <span
                className={cn(
                  'text-m font-semibold',
                  pressureSensorError ? 'text-red-600' : 'text-green-600'
                )}
              >
                {pressureSensorError ? '이상' : '정상'}
              </span>
            </div>
          </div>
        } */}

        {/* 예비1 */}
        {/* {
          <div className="bg-white rounded-lg p-3 border border-gray-200">
            <div className="flex items-center justify-between">
              <span className="text-m text-gray-600">
                {alias.preparation || '예비1'}
              </span>
              <span
                className={cn(
                  'text-m font-semibold',
                  preparation ? 'text-red-600' : 'text-green-600'
                )}
              >
                {preparation ? '이상' : '정상'}
              </span>
            </div>
          </div>
        } */}

        {/* 예비2 */}
        {/* {
          <div className="bg-white rounded-lg p-3 border border-gray-200">
            <div className="flex items-center justify-between">
              <span className="text-m text-gray-600">
                {alias.preparation2 || '예비2'}
              </span>
              <span
                className={cn(
                  'text-m font-semibold',
                  preparation2 ? 'text-red-600' : 'text-green-600'
                )}
              >
                {preparation2 ? '이상' : '정상'}
              </span>
            </div>
          </div>
        } */}

        {/* 데이터가 없는 경우 */}
        {!chemicalLevel &&
          undergroundWaterLevel === undefined &&
          (!temperature || temperature === 0) &&
          (!inflow || inflow === 0) && (
            <div className="text-center text-xs text-gray-400 py-2">
              추가 센서 데이터 없음
            </div>
          )}
      </div>
    </div>
  );
}
